import { FC, useEffect, useState } from 'react';
import { Table } from 'shared/components';
import { useVerifyAction } from '@vadiun/react-hooks-legacy';
import {
  Contingency,
  ContingencyReason,
  ContingencyType,
  contingencyService
} from 'app/viaje/viajeTraslado/coordinar/services/contingencyService';
import { ContingencyColumnVisibility } from '../hooks/useContingencies';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('es-AR', { hour12: false });
};

interface Props {
  contingencies: Contingency[];
  total: number;
  page: number;
  rowsPerPage: number;
  sort: { sortBy: string; sortDir: 'ASC' | 'DESC' };
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  onSortChange: (sortBy: string, direction: 'asc' | 'desc' | null) => void;
  onEdit: (contingency: Contingency) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  columnVisibility: ContingencyColumnVisibility;
}

const ContingenciesTable: FC<Props> = ({
  contingencies,
  total,
  page,
  rowsPerPage,
  sort,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange,
  onEdit,
  onDelete,
  isDeleting,
  columnVisibility
}) => {
  const verifyAction = useVerifyAction();
  const service = contingencyService();
  const [reasonsMap, setReasonsMap] = useState<Record<number, string>>({});
  const [typesMap, setTypesMap] = useState<Record<number, string>>({});

  useEffect(() => {
    service
      .getContingencyReasons()
      .then((rows: ContingencyReason[]) => {
        setReasonsMap(
          rows.reduce((acc, r) => {
            acc[r.id] = r.label;
            return acc;
          }, {} as Record<number, string>)
        );
      })
      .catch(() => {});
    service
      .getContingencyTypes()
      .then((rows: ContingencyType[]) => {
        setTypesMap(
          rows.reduce((acc, t) => {
            acc[t.id] = t.label;
            return acc;
          }, {} as Record<number, string>)
        );
      })
      .catch(() => {});
  }, []);

  const handleDelete = (id: number) => {
    verifyAction({
      title: '¡Estás por eliminar una contingencia!',
      body: 'Esta acción eliminará la contingencia de forma permanente. ¿Estás seguro?',
      onAccept: () => onDelete(id)
    });
  };

  const columns = [
    {
      name: 'contingency_reason_id',
      label: 'Razón',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.contingency_reason_id,
        customBodyRender: (id: number) => reasonsMap[id] ?? id ?? '-'
      }
    },
    {
      name: 'contingency_type_id',
      label: 'Tipo',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.contingency_type_id,
        customBodyRender: (id: number) => typesMap[id] ?? id ?? '-'
      }
    },
    {
      name: 'applied_at',
      label: 'Aplicada',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.applied_at,
        customBodyRender: (value: string) => formatDate(value)
      }
    },
    {
      name: 'finished_at',
      label: 'Finaliza',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.finished_at,
        customBodyRender: (value: string | null) => formatDate(value)
      }
    },
    {
      name: 'duration_minutes',
      label: 'Duración (min)',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.duration_minutes,
        customBodyRender: (value: number | null) => value ?? '-'
      }
    },
    {
      name: 'applied_by_user_name',
      label: 'Usuario',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.applied_by_user_name,
        customBodyRender: (value: string | null) => value ?? '-'
      }
    },
    {
      name: 'polygon_zone_id',
      label: 'Polígono',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.polygon_zone_id,
        customBodyRender: (value: number | null) => value ?? '-'
      }
    },
    {
      name: 'observation',
      label: 'Observación',
      options: {
        filter: false,
        sort: false,
        display: columnVisibility.observation,
        customBodyRender: (value: string | null) => value ?? '-'
      }
    },
    {
      name: 'updated_at',
      label: 'Actualizada',
      options: {
        filter: false,
        sort: true,
        display: columnVisibility.updated_at,
        customBodyRender: (value: string) => formatDate(value)
      }
    },
  ];

  const data = contingencies.map((c) => ({ ...c }));

  return (
    <div className="w-full">
      <Table
        title=""
        data={data}
        columns={columns}
        options={{
          filter: false,
          search: false,
          download: false,
          print: false,
          elevation: 0,
          selectableRows: 'none',
          viewColumns: false,
          serverSide: true,
          count: total,
          page: page - 1,
          rowsPerPage,
          sortOrder: {
            name: sort.sortBy,
            direction: sort.sortDir.toLowerCase() as 'asc' | 'desc'
          },
          onChangePage(nextPage: number) {
            onChangePage(nextPage + 1);
          },
          onChangeRowsPerPage(nextRowsPerPage: number) {
            onChangeRowsPerPage(nextRowsPerPage);
          },
          onColumnSortChange(changedColumn: string, direction: 'asc' | 'desc') {
            onSortChange(changedColumn, direction);
          }
        }}
      />
    </div>
  );
};

export default ContingenciesTable;
